import h from 'virtual-dom/h';
import diff from 'virtual-dom/diff';
import patch from 'virtual-dom/patch';
import createElement from 'virtual-dom/create-element';
import { orderBy, flattenDeep, merge } from 'lodash-es';
import {LocalStorageManager} from "./custom/localStorageManager";

export interface RepeaterOptions {
    listViewId: string;
    rootElementId: string;
    wrapperElement?: ((items: (string | VirtualDOM.VNode)[]) => VirtualDOM.VNode);
    getItems: ((pageNumber?: number, pageSize?: number, searchStrings?: Record<string, string>, sortOrders?: [string, "asc" | "desc"][]) => Promise<any[] | PagedResults>);
    preProcessItems?: ((items: any[]) => any[]);
    filterId?: string | string[];
    useDefaultFilter?: boolean;
    customFilter?: ((items: any[]) => any[]);
    defaultOrder?: [string, 'asc' | 'desc'][];
    customOrder?: ((items: any[]) => any[]);
    renderItem: ((item: any) => VirtualDOM.VNode);
    noResults?: (() => VirtualDOM.VNode);
    postRender?: ((items: any[]) => void);
    pagerOptions?: PagerOptions;
};

export interface PagerOptions {
    paging?: boolean;
    pageSize?: number;
    pagerElementId?: string;
    adjacentPages?: number;
    renderPages?: (() => VirtualDOM.VNode);
    onPageChange?: ((page: number) => void);
    startPage?: number;
    serverSidePaging?: boolean;
}

export let repeaterDefaults: any = {
    useDefaultFilter: true,
    pagerOptions: {
        paging: true,
        pageSize: 50,
        adjacentPages: 3,
        serverSidePaging: false
    }
};

export interface PagedResults {
    items: any[];
    totalCount: number;
    pageNumber: number;
    pageSize: number;
}

interface LocalStorageFilter {
    filterId: string;
    value: any;
}

export class Repeater {
    public initialized: boolean = false;
    public options: RepeaterOptions; // The default options combined with inputted options
    public filters: HTMLInputElement[]; // Filter element, if filterId was given
    public sortOrders: [string, 'asc' | 'desc'][]
    public items: any[]; // All items
    public preProcessedItems: any[]; // Items after preprocessing
    public processedItems: any[]; // Items after preprocessing, filtering, and ordering
    public pagedItems: any[]; // Items shown on current page
    public rootVirtualElement: VirtualDOM.VNode; // The virtual root element of the repeater
    public rootDomElement: Element; // The actual root dom element of the repeater
    public pagerVirtualElement: VirtualDOM.VNode; // the virtual root element of the pager
    public pagerDomElement: Element; // The actual root dom element of the pager
    public page: number = 1; // The current page
    public totalItemsCount: number = 0; // The total number of items, if server side paging is used
    public serverSideFilters: Record<string, string> = {}; // The server side filter, if used
    public listViewPageKey: string;
    public localStorageManager: LocalStorageManager;

    /**
     * Initializes repeater
     * Extends given options with the default options and sets the default sort order
     */
    constructor(options: RepeaterOptions) {
        this.localStorageManager = new LocalStorageManager(options.listViewId);
        this.listViewPageKey = `${options.listViewId}-page`;
        let extendedOptions = this.initExtendedOptions();
        this.options = merge({}, repeaterDefaults, extendedOptions, options);
        
        const localStorageSortOrders = this.localStorageManager.getNestedItem<[string, 'asc' | 'desc'][]>('sortOrders');
        this.sortOrders = localStorageSortOrders ?? this.options.defaultOrder ?? [];
        
        if(this.options.pagerOptions?.serverSidePaging && this.options.filterId) {
            if(Array.isArray(this.options.filterId)) {
                this.options.filterId.forEach(filterId => {
                    const filter = this.getUrlParameter(filterId);
                    if(filter) {
                        this.serverSideFilters[filterId] = filter;
                    }
                });
            } else {
                const filter = this.getUrlParameter(this.options.filterId);
                if(filter) {
                    this.serverSideFilters[this.options.filterId] = filter;
                }
            }
        }

        const urlPage = this.getUrlParameter(this.listViewPageKey);
        if(urlPage) {
            this.page = parseInt(urlPage);
        } else {
            this.page = options.pagerOptions?.startPage ?? 1;
        }
    }

    /**
     * Allows extensions to set default options
     */
    protected initExtendedOptions(): any {

    }

    /**
     * Initializes filter and rootElements
     * Will be called on initial call of load()
     */
    protected initialize(): void {
        this.initFilter();
        this.createRootElements();
        this.initialized = true;
    }

    /**
     * Initialize the filter if filterId is given
     * Stores filter element in the repeater and will add an onchange event that calls the build process
     * Will throw exception if element with filterId is not found
     */
    protected initFilter(): void {
        if (this.options.filterId) {
            if(Array.isArray(this.options.filterId)) {
                const filterElements: HTMLInputElement[] = [];
                this.options.filterId.forEach(filterId => {
                    let filter = document.getElementById(filterId);
                    if (!filter) {
                        throw new Error(`No element with FilterId ${filterId} could be found`);
                    }
                    
                    const filterElement = <HTMLInputElement>filter;
                    filterElement.onchange = () => {
                        this.setUrlParameter(filterId, filterElement.value);

                        if (this.options.pagerOptions?.serverSidePaging) {
                            // Update server-side filters
                            if(filterElement.value) {
                                this.serverSideFilters[filterId] = filterElement.value;
                            } else {
                                delete this.serverSideFilters[filterId];
                            }
                            this.page = 1;
                            this.setUrlParameter(this.listViewPageKey, null);
                            this.load();
                            return;
                        }

                        this.build();
                    }
                    
                    const urlFilter = this.getUrlParameter(filterId);
                    if(urlFilter) {
                        filterElement.value = urlFilter;
                    }
                    
                    filterElements.push(filterElement);
                });
                this.filters = filterElements;
            } else {
                let filter = document.getElementById(this.options.filterId);
                if (!filter) {
                    throw new Error('No element with FilterId could be found');
                }
                
                const filterElement = <HTMLInputElement>filter;
                const filterId = this.options.filterId;

                filterElement.onchange = () => {
                    this.setUrlParameter(filterId, filterElement.value);

                    if (this.options.pagerOptions?.serverSidePaging) {
                        if(filterElement.value) {
                            this.serverSideFilters[filterId] = filterElement.value;
                        } else {
                            delete this.serverSideFilters[filterId];
                        }
                        this.page = 1;
                        this.setUrlParameter(this.listViewPageKey, null);
                        this.load();
                        return;
                    }
                    
                    this.build();
                }

                const urlFilter = this.getUrlParameter(filterId);
                if(urlFilter) {
                    filterElement.value = urlFilter;
                }

                this.filters = [filterElement];
            }
        }
    }

    /**
     * Returns the element with rootElementId
     * Throws error if that element is not found
     */
    protected baseElement(): HTMLElement {
        let baseElement = document.getElementById(this.options.rootElementId);
        if (baseElement) {
            return baseElement;
        }

        throw new Error('No element with RootElementId could be found');
    }

    /**
     * Returns the element with pagerElementId
     * Returns null if no pagerElementId is given
     * Throws error if element with pagerElementId is not found
     */
    protected pagerElement(): HTMLElement | null {
        let pagerElement = null;

        if (this.options.pagerOptions && this.options.pagerOptions.pagerElementId) {
            pagerElement = document.getElementById(this.options.pagerOptions.pagerElementId);
            if (!pagerElement) {
                throw new Error('No element with PagerElementId could be found');
            }
        }

        return pagerElement;
    }

    /**
     * Creates a root virtual element which holds the rendered items
     * If wrapperElement option is given, that function is called
     * Otherwise will return the default wrapper element
     */
    protected createVirtualRootElement(elements: (string | VirtualDOM.VNode)[]): VirtualDOM.VNode {
        return this.options.wrapperElement
            ? this.options.wrapperElement(elements)
            : this.wrapperElement(elements);
    }

    /**
     * Default wrapper element generator
     * Generates a div element
     */
    protected wrapperElement(elements: (string | VirtualDOM.VNode)[]): VirtualDOM.VNode {
        return h('div', elements);
    }

    /**
     * Creates the root elements for the repeater and the pager
     * Virtual elements and dom elements are stored in repeater
     * Pager elements are created only if needed
     */
    protected createRootElements(): void {
        let baseElement = this.baseElement();
        this.rootVirtualElement = this.createVirtualRootElement(['']);
        this.rootDomElement = createElement(this.rootVirtualElement);
        baseElement.appendChild(this.rootDomElement);

        let pagerElement = this.pagerElement();
        if (pagerElement) {
            this.pagerVirtualElement = h('div', ['']);
            this.pagerDomElement = createElement(this.pagerVirtualElement);
            pagerElement.appendChild(this.pagerDomElement);
        }
    }

    /**
     * Resets the dom element and root virtual element to initial state
     * Do this if you manually make changes to the real dom and the virtual dom does not match anymore
     */
    public reset(): void {
        while (this.rootDomElement.lastChild) {
            this.rootDomElement.removeChild(this.rootDomElement.lastChild);
        }
        this.rootDomElement.appendChild(document.createTextNode(''));
        this.rootVirtualElement = this.createVirtualRootElement(['']);
    }

    /**
     * Will call initialize() if not yet initialized
     * Loads the data with getItems function and preprocesses it
     * After that, build will be called
     */
    public load(): void {
        if (!this.initialized) {
            this.initialize();
        }

        this.getItems()
            .then(result => {
                if(this.options.pagerOptions?.serverSidePaging) {
                    const pagedResult = result as PagedResults;
                    this.items = pagedResult.items;
                    this.totalItemsCount = pagedResult.totalCount;
                } else {
                    this.items = result as any[];
                }

                this.preProcessedItems = this.preProcessItems([...this.items]);

                this.build();
            }, err => {
                console.log(err);
            });
    }

    /**
     * General build function for the repeater
     * Will filter, order the items, correct the current page if needed,
     * determine the items to show on the page, render all the items and pager
     * And will call the postRender
     */
    public build(): void {
        this.processedItems = this.filterItems([...this.preProcessedItems]);
        this.processedItems = this.orderItems([...this.processedItems]);
        this.correctCurrentPage();
        this.pagedItems = this.pageItems([...this.processedItems]);
        this.render(this.pagedItems);
        this.renderPager();
        this.postRender(this.processedItems);
    }

    /**
     * Gets the items for the repeater
     */
    protected async getItems(): Promise<any[] | PagedResults> {
        if (this.options.pagerOptions?.serverSidePaging) {
            const searchStrings = Object.keys(this.serverSideFilters).length > 0
                ? this.serverSideFilters
                : undefined;

            return this.options.getItems(
                this.page,
                this.options.pagerOptions.pageSize,
                searchStrings,
                this.sortOrders
            );
        }

        return this.options.getItems();
    }

    /**
     * Will call preprocess function if given
     */
    protected preProcessItems(items: any[]): any[] {
        if (this.options.preProcessItems) {
            return this.options.preProcessItems(items);
        }

        return items;
    }

    /**
     * Filter the items
     * If defaultFilter options is given, will first perform a default filtering
     * After that, if custom filter is given, will perform that filter as well
     */
    protected filterItems(items: any[]): any[] {
        if (this.options.useDefaultFilter) {
            items = this.defaultFilter(items);
        }

        if (this.options.customFilter) {
            items = this.options.customFilter(items);
        }

        return items;
    }

    /**
     * Default filter function
     * Will create an array of all values in the object, arrays will be flattened
     * Only filters on strings and numbers
     * Values and filterValue are lowercased
     * Searches through all the values for the filterValue
     */
    protected defaultFilter(items: any[]): any[] {
        if (this.filters) {
            
            for(let filter of this.filters) {
                let filterValue = filter.value;
                if (filterValue) {
                    items = items.filter(item => {
                        let values = Object.keys(item).map(key => item[key]);
                        values = flattenDeep(values);
                        values = values
                            .filter(value => typeof value === 'string'
                                || typeof value === 'number')
                            .map(value => value.toString().toLowerCase());
                        return values.some(value => value.includes(filterValue.toLowerCase()));
                    });
                }
            }
        }

        return items;
    }

    /**
     * Orders the items
     * Uses a customOrder if given, otherwise uses defaultOrder
     */
    protected orderItems(items: any[]): any[] {
        if (this.options.customOrder) {
            return this.options.customOrder(items);
        }

        return this.defaultOrder(items);
    }

    /**
     * Default order function
     * Will order based on properties and sortOrders
     * See lodash orderby documentation
     */
    protected defaultOrder(items: any[]): any[] {
        if (this.sortOrders.length > 0) {
            let properties = this.sortOrders.map(order => order[0]);
            let sortOrders = this.sortOrders.map(order => order[1]);
            return orderBy(items, properties, sortOrders);
        }

        return items;
    }

    /**
     * Corrects the current page number based on the total pages
     * If currentPage is higher than the totalpages, currentPage is set to 1
     */
    protected correctCurrentPage(): void {
        const totalPages = this.totalPages();
        if (this.page > totalPages) {
            this.page = 1;
            this.setUrlParameter(this.listViewPageKey, null);
        }
    }

    /**
     * Returns items to show on the current page
     * This is based on the given pageSize
     */
    protected pageItems(items: any[]): any[] {
        if (this.options.pagerOptions?.serverSidePaging) {
            return items;
        }
        
        if (this.options.pagerOptions && this.options.pagerOptions.paging && this.options.pagerOptions.pageSize) {
            const pageSize = this.options.pagerOptions.pageSize;
            let start = (this.page - 1) * pageSize;
            let end = start + pageSize;
            items = items.slice(start, end);
        }

        return items;
    }

    /**
     * Renders the repeater
     */
    protected render(items: any[]): void {
        let elements = this.renderData(items);
        let newRootElement = this.createVirtualRootElement(elements);
        let patches = diff(this.rootVirtualElement, newRootElement);
        this.rootDomElement = patch(this.rootDomElement, patches);

        this.rootVirtualElement = newRootElement;
    }

    /**
     * Renders all data to show using virtual-dom
     * If no items are present, it will call options.noResults if present
     */
    protected renderData(items: any[]): VirtualDOM.VNode[] {
        if (items.length === 0 && this.options.noResults) {
            let noResults = this.options.noResults();
            return [noResults];
        }

        let elements = items.map(this.options.renderItem);
        return elements;
    }

    /**
     * Renders the pager using virtual-dom
     * Calls custom renderPages if present, otherwise uses default
     * Sets the pagerDomElement and pagerVirtualElement
     */
    protected renderPager(): void {
        let newPagerElement: VirtualDOM.VNode;
        if (this.options.pagerOptions && this.options.pagerOptions.paging) {
            if (this.options.pagerOptions.renderPages) {
                newPagerElement = this.options.pagerOptions.renderPages();
            } else {
                newPagerElement = this.renderDefaultPager();
            }

            let patches = diff(this.pagerVirtualElement, newPagerElement);
            this.pagerDomElement = patch(this.pagerDomElement, patches);
            this.pagerVirtualElement = newPagerElement;
        }
    }

    /**
     * If there is only 1 page or no pages, no pager will be shown
     * Otherwise will render go to first page, go to previous page,
     * go to next page, go to last page buttons,
     * and buttons for page numbers that should be shown
     */
    protected renderDefaultPager(): VirtualDOM.VNode {
        if (this.totalPages() < 2) {
            return h('div', ['']);
        }

        let pagerButtons: VirtualDOM.VNode[] = [];
        pagerButtons.push(this.goToFirstPageButton());
        pagerButtons.push(this.goToPreviousPageButton());

        pagerButtons.push(...this.showingPageNumbers()
            .map(pageNumber => this.goToPageButton(pageNumber)));

        pagerButtons.push(this.goToNextPageButton());
        pagerButtons.push(this.goToLastPageButton());

        let newPagerElement = h('div', [
            h('ul', { className: 'pagination' }, pagerButtons)
        ]);

        return newPagerElement;
    }

    /**
     * Will render a button for the pager to go to the first page
     * If current page is first page, this button will be disabled
     */
    protected goToFirstPageButton(): VirtualDOM.VNode {
        const disabled = this.page === 1;
        return h('li', {
            className: disabled ? 'disabled' : '',
            onclick: (event: any) => {
                event.preventDefault();
                this.goToFirstPage();
            }
        }, [h('a', { href: '#' }, '«')]);
    }

    /**
     * Will render a button for the pager to go to the previous page
     * If current page is first page, this button will be disabled
     */
    protected goToPreviousPageButton(): VirtualDOM.VNode {
        const disabled = this.page === 1;
        return h('li', {
            className: disabled ? 'disabled' : '',
            onclick: (event: any) => {
                event.preventDefault();
                this.goToPreviousPage();
            }
        }, [h('a', { href: '#' }, '‹')]);
    }

    /**
     * Will render a button for the pager to go to the page number
     * Will be set active if current page is equal to input page number
     */
    protected goToPageButton(page: number): VirtualDOM.VNode {
        const active = this.page === page;
        return h('li', {
            className: active ? 'active' : '',
            onclick: (event: any) => {
                event.preventDefault();
                this.goToPage(page);
            }
        }, [h('a', { href: '#' }, page.toString())]);
    }

    /**
     * Will render a button for the pager to go to the next page
     * If current page is last page, this button will be disabled
     */
    protected goToNextPageButton(): VirtualDOM.VNode {
        const disabled = this.page === this.totalPages();
        return h('li', {
            className: disabled ? 'disabled' : '',
            onclick: (event: any) => {
                event.preventDefault();
                this.goToNextPage();
            }
        }, [h('a', { href: '#' }, '›')]);
    }

    /**
     * Will render a button for the pager to go to the last page
     * If current page is last page, this button will be disabled
     */
    protected goToLastPageButton(): VirtualDOM.VNode {
        const disabled = this.page === this.totalPages();
        return h('li', {
            className: disabled ? 'disabled' : '',
            onclick: (event: any) => {
                event.preventDefault();
                this.goToLastPage();
            }
        }, [h('a', { href: '#' }, '»')]);
    }

    /**
     * Will call the postRender function, if given
     */
    protected postRender(items: any[] = []): void {
        if (this.options.postRender) {
            this.options.postRender(items);
        }
    }

    /**
     * if paging is enabled, will set current page to input page number
     * Only if input is valid
     * Sets page and calls the build function if successfull
     */
    public goToPage(page: number): void {
        if (this.options.pagerOptions && this.options.pagerOptions.paging) {
            const totalPages = this.totalPages();
            if (page > 0 && page <= totalPages) {
                this.page = page;
                this.setUrlParameter(this.listViewPageKey, page.toString());
                
                if(this.options.pagerOptions.serverSidePaging) {
                    this.load();
                } else {
                    this.build();
                }

                if (this.options.pagerOptions.onPageChange != null) {
                    this.options.pagerOptions.onPageChange(page);
                }
            }
        }
    }

    /**
     * Sets current page to the first page
     */
    public goToFirstPage(): void {
        this.goToPage(1);
    }

    /**
     * Sets current page to the last page
     */
    public goToLastPage(): void {
        const totalPages = this.totalPages();
        this.goToPage(totalPages);
    }

    /**
     * Decrements current page number
     */
    public goToPreviousPage(): void {
        this.goToPage(this.page - 1);
    }

    /**
     * Increments current page number
     */
    public goToNextPage(): void {
        this.goToPage(this.page + 1);
    }

    /**
     * Returns the total number of pages based on pageSize and filtered items
     */
    protected totalPages(): number {
        let total = 1;

        if (this.options.pagerOptions && this.options.pagerOptions.paging && this.options.pagerOptions.pageSize) {
            const pageSize = this.options.pagerOptions.pageSize;
            const totalItems = this.options.pagerOptions.serverSidePaging ? this.totalItemsCount : this.processedItems.length;
            total = Math.ceil(totalItems / pageSize);
        }

        return total;
    }

    /**
     * Returns a list of numbers that should be shown in the pager
     * Based on the adjacentPages setting
     */
    protected showingPageNumbers(): number[] {
        let result: number[] = [];

        if (this.options.pagerOptions && this.options.pagerOptions.paging && this.options.pagerOptions.adjacentPages) {
            const adjacentPages = this.options.pagerOptions.adjacentPages;
            let minPage = Math.max(this.page - adjacentPages, 1);

            const totalPages = this.totalPages();
            let maxPage = Math.min(this.page + adjacentPages, totalPages);

            for (var i = minPage; i <= maxPage; i++) {
                result.push(i);
            }
        }

        return result;
    }
    
    private setUrlParameter(key: string, value: string | null) {
        const url = new URL(window.location.href);
        if (value === null || value === '') {
            url.searchParams.delete(key);
        } else {
            url.searchParams.set(key, value);
        }
        window.history.replaceState({}, '', url.toString());
    }
    
    private getUrlParameter(key: string): string | null {
        const url = new URL(window.location.href);
        return url.searchParams.get(key);
    }
}
